import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import { formaterDateHeure } from '../lib/format';
import { Badge } from './Elements';
import type { EtatCompteGoogle } from '../types';

/**
 * LE COMPTE TECHNIQUE GOOGLE (ADR-0014, révision du 2026-10-03) : le
 * compte Workspace au nom duquel l'application dépose les documents sur
 * les Drives partagés — et enverra les mails. Le super admin le connecte
 * une fois ; le parcours est une navigation vers Google, le retour
 * revient ici avec un résultat dans l'URL.
 */
export function CompteGoogle() {
  const { peut } = useAuth();
  const [parametres, setParametres] = useSearchParams();
  const etat = useRessource(() => api.get<EtatCompteGoogle>('/api/admin/google/drive'), []);
  const [message, setMessage] = useState<{ ton: 'ok' | 'erreur'; texte: string } | null>(null);
  const [enCours, setEnCours] = useState(false);

  // Le retour du parcours Google : un mot dans l'URL, lu une fois puis effacé.
  useEffect(() => {
    const resultat = parametres.get('drive');
    if (!resultat) return;
    const compte = parametres.get('compte');
    const textes: Record<string, { ton: 'ok' | 'erreur'; texte: string }> = {
      connecte: { ton: 'ok', texte: `Compte connecté${compte ? ` : ${compte}` : ''}.` },
      refuse: { ton: 'erreur', texte: 'Autorisation refusée dans la fenêtre Google.' },
      non_configure: { ton: 'erreur', texte: 'Le client OAuth Google n’est pas configuré sur ce serveur.' },
      erreur: { ton: 'erreur', texte: 'La connexion a échoué. Le détail est dans les journaux du serveur.' },
    };
    setMessage(textes[resultat] ?? textes.erreur!);
    setParametres({}, { replace: true });
  }, [parametres, setParametres]);

  const deconnecter = async () => {
    if (!window.confirm('Déconnecter le compte Google ? Les archivages en attente resteront en attente.')) return;
    setEnCours(true);
    try {
      await api.delete('/api/admin/google/drive');
      setMessage({ ton: 'ok', texte: 'Compte déconnecté.' });
      etat.recharger();
    } catch (cause) {
      setMessage({ ton: 'erreur', texte: cause instanceof ErreurApi ? cause.message : 'Déconnexion impossible.' });
    } finally {
      setEnCours(false);
    }
  };

  const connexion = etat.donnees?.connexion ?? null;
  const peutGerer = peut('parametre_systeme.gerer');

  return (
    <section className="carte space-y-3 p-4">
      <h2 className="text-xs font-medium uppercase tracking-wide text-encre-500">Compte technique Google</h2>
      <p className="text-sm text-encre-600">
        Le compte Workspace au nom duquel l’application dépose les documents sur les Drives partagés des
        écoles et envoie leur fiche de paie aux salariés. Il doit être membre <strong>Contributeur</strong>{' '}
        de chaque Drive, dont l’identifiant se règle sur la fiche de l’école.
      </p>

      {etat.donnees && !etat.donnees.configure && (
        <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Le client OAuth Google n’est pas configuré sur ce serveur : rien ne peut se connecter.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        {connexion ? (
          <>
            <Badge ton="succes">connecté</Badge>
            <span className="font-medium">{connexion.compte}</span>
            <span className="text-xs text-encre-500">
              depuis le {formaterDateHeure(connexion.connecte_le)}
              {connexion.derniere_utilisation
                ? ` · dernière utilisation le ${formaterDateHeure(connexion.derniere_utilisation)}`
                : ' · jamais utilisé'}
            </span>
          </>
        ) : (
          <>
            <Badge ton="neutre">non connecté</Badge>
            <span className="text-xs text-encre-500">aucun document ne peut être archivé sur un Drive.</span>
          </>
        )}
      </div>

      {etat.donnees?.aReconnecter && (
        <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {etat.donnees.motifReconnexion === 'jeton'
            ? 'Le jeton de ce compte ne se déchiffre pas avec la clé de ce serveur — une base restaurée depuis un autre serveur, ou un secret de session changé. Rien ne peut être lu ni archivé sur le Drive tant que le compte n’est pas reconnecté.'
            : 'Ce compte a été connecté avec un accès limité aux fichiers créés par l’application. L’archivage dans vos propres dossiers et la lecture des fiches de paie demandent l’accès complet au Drive : reconnectez le compte.'}
        </p>
      )}

      {connexion && !etat.donnees?.aReconnecter && !etat.donnees?.peutEnvoyer && (
        <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Ce compte lit et archive sur le Drive, mais n’a pas encore le droit d’envoyer des messages. Reconnectez-le une
          fois pour que les fiches de paie puissent partir aux salariés.
        </p>
      )}

      {connexion?.derniere_erreur && (
        <p className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800">
          Dernière erreur : {connexion.derniere_erreur}
        </p>
      )}

      {message && (
        <p
          className={`rounded-md border p-3 text-sm ${
            message.ton === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
          role="status"
        >
          {message.texte}
        </p>
      )}

      {peutGerer && (
        <div className="flex flex-wrap gap-2">
          {/* Une NAVIGATION, pas un fetch : le parcours passe par la fenetre
              de consentement Google puis revient ici. */}
          <a
            href="/api/admin/google/drive/depart"
            className={`bouton-principal ${etat.donnees && !etat.donnees.configure ? 'pointer-events-none opacity-50' : ''}`}
          >
            {connexion
              ? etat.donnees?.motifReconnexion === 'perimetre'
                ? 'Reconnecter avec l’accès complet'
                : etat.donnees?.aReconnecter
                  ? 'Reconnecter le compte'
                  : 'Reconnecter un compte'
              : 'Connecter le compte Google'}
          </a>
          {connexion && (
            <button type="button" className="bouton-secondaire" disabled={enCours} onClick={() => void deconnecter()}>
              Déconnecter
            </button>
          )}
        </div>
      )}

      <p className="text-xs text-encre-500">
        Dans la fenêtre Google, connectez-vous au compte technique (pas à votre compte) et acceptez l’accès au
        Drive et l’envoi de messages. Le compte ne voit que les Drives partagés dont il est membre. Le jeton est conservé chiffré ;
        changer le secret de session du serveur oblige à reconnecter.
      </p>
    </section>
  );
}
